// Global Instructions Rule Applied!

import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

// Read shape of one Email Design Template row.
//
// Single superset model — same approach as email_campaign_grid_model.ts.
// Grid uses the lean subset of keys; cold-load detail also carries
// body_html. Detail-only keys are .optional() so a grid row passes
// validation without carrying them.
//
// .strict() rejects unknown keys, so a future schema addition fails loudly
// until the model is extended (CLAUDE.md: a response that fails validation
// is an error, not coerced).

// Same element shape as EmailCampaignAttachmentSchema — template
// attachments are copied onto campaigns when a template is applied, so
// both lists render through the same UI affordances.
export const EmailDesignTemplateAttachmentSchema = z
  .object({
    id: z.string(),
    file_name: z.string(),
    file_url: z.string(),
    date_created: z.string()
  })
  .strict()

export const EmailDesignTemplateModelSchema = z
  .object({
    email_design_templates__id: z.string(),
    email_design_templates__name: z.string(),
    email_design_templates__subject_default: z.string(),
    email_design_templates__body_source: z.string(),
    email_design_templates__body_size_kb: z.number(),
    email_design_templates__date_created: z.string(),
    email_design_templates__date_modified: z.string(),
    // Detail-only
    email_design_templates__body_html: z.string().nullable().optional(),
    email_design_templates__attachments: z
      .array(EmailDesignTemplateAttachmentSchema)
      .nullable()
      .optional()
  })
  .strict()

export type EmailDesignTemplateAttachment = z.infer<
  typeof EmailDesignTemplateAttachmentSchema
>

export type EmailDesignTemplateModel = z.infer<typeof EmailDesignTemplateModelSchema>

export const EmailDesignTemplateFields = makeFieldKeys(
  EmailDesignTemplateModelSchema
)

// Filter-options shape: { email_design_templates__body_source: [{value,label}] }
export const EmailDesignTemplateFilterOptionSchema = z
  .object({
    value: z.string(),
    label: z.string()
  })
  .strict()

export const EmailDesignTemplateFilterOptionsSchema = z
  .object({
    email_design_templates__body_source: z.array(
      EmailDesignTemplateFilterOptionSchema
    )
  })
  .strict()

export type EmailDesignTemplateFilterOptions = z.infer<
  typeof EmailDesignTemplateFilterOptionsSchema
>

export function validateEmailDesignTemplate(
  data: unknown,
  options?: { isArray?: boolean }
): EmailDesignTemplateModel | EmailDesignTemplateModel[] {
  return validateWithZodSchema(
    EmailDesignTemplateModelSchema,
    data,
    'email design template model',
    options
  ) as EmailDesignTemplateModel | EmailDesignTemplateModel[]
}

export function validateEmailDesignTemplateFilterOptions(
  data: unknown
): EmailDesignTemplateFilterOptions {
  return validateWithZodSchema(
    EmailDesignTemplateFilterOptionsSchema,
    data,
    'email design template filter options'
  ) as EmailDesignTemplateFilterOptions
}
